import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { QRCodeSVG } from 'qrcode.react';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import { formatPhone } from '../utils/formatPhone';

export default function Dashboard() {
  const { qrCode, waStatus } = useSocket();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentChats, setRecentChats] = useState([]);

  useEffect(() => {
    api.get('/api/leads/stats').then(({ data }) => setStats(data)).catch(() => {});
    api.get('/api/chats?limit=5').then(({ data }) => setRecentChats(data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (waStatus === 'ready') {
      api.get('/api/leads/stats').then(({ data }) => setStats(data)).catch(() => {});
      api.get('/api/chats?limit=5').then(({ data }) => setRecentChats(data.data || [])).catch(() => {});
    }
  }, [waStatus]);

  const statCards = stats
    ? [
        { label: 'Total Leads', value: stats.total, icon: '👥', color: '#6366f1' },
        { label: 'Interested', value: stats.interested, icon: '🔥', color: '#f97316' },
        { label: 'Follow-up', value: stats.followUp, icon: '📞', color: '#facc15' },
        { label: 'Enrolled', value: stats.enrolled, icon: '🎓', color: '#22c55e' },
        { label: 'Want Human', value: stats.wantsHuman, icon: '🙋', color: '#ec4899' },
      ]
    : [];

  const waStatusConfig = {
    ready:         { bg: '#0d2b1a', border: '#22c55e', color: '#86efac', icon: '✅', label: 'CONNECTED', text: 'Bot is live and auto-replying to incoming messages.' },
    authenticated: { bg: '#1a1f0a', border: '#facc15', color: '#fef08a', icon: '🔄', label: 'AUTHENTICATING', text: 'Session confirmed — finalising connection, almost there...' },
    qr_ready:      { bg: '#1a150a', border: '#f97316', color: '#fed7aa', icon: '📱', label: 'SCAN QR CODE', text: 'Scan the QR code below with your WhatsApp to go live.' },
    disconnected:  { bg: '#1f0d0d', border: '#ef4444', color: '#fca5a5', icon: '⚠️',  label: 'DISCONNECTED', text: 'Go to Bot & WhatsApp to reconnect your WhatsApp.' },
  };
  const waCfg = waStatusConfig[waStatus] || waStatusConfig.disconnected;

  return (
    <div style={styles.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 20 }}>
        <h1 style={styles.title}>Dashboard</h1>
        <span style={{ color: '#64748b', fontSize: 14 }}>Welcome back, {user?.name}</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', borderRadius: 10, border: `1px solid ${waCfg.border}`, background: waCfg.bg, marginBottom: 24 }}>
        <span style={{ fontSize: 28, flexShrink: 0 }}>{waCfg.icon}</span>
        <div>
          <div style={{ fontWeight: 700, color: waCfg.color, fontSize: 13, letterSpacing: 0.5 }}>WhatsApp — {waCfg.label}</div>
          <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 2 }}>{waCfg.text}</div>
        </div>
      </div>

      {waStatus === 'qr_ready' && qrCode && (
        <div style={styles.qrContainer}>
          <h2 style={{ margin: '0 0 8px', color: '#f1f5f9' }}>Scan with WhatsApp</h2>
          <p style={{ color: '#94a3b8', marginBottom: 16, fontSize: 14 }}>Open WhatsApp → Linked Devices → Link a Device → scan below.</p>
          <div style={styles.qrCodeWrapper}>
            <QRCodeSVG value={qrCode} size={256} />
          </div>
        </div>
      )}

      <div style={styles.statsGrid}>
        {statCards.map((s) => (
          <div key={s.label} style={{ ...styles.statCard, borderTop: `3px solid ${s.color}` }}>
            <span style={styles.statIcon}>{s.icon}</span>
            <span style={styles.statValue}>{s.value ?? '—'}</span>
            <span style={styles.statLabel}>{s.label}</span>
          </div>
        ))}
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Recent Conversations</h2>
        {recentChats.length === 0 ? (
          <p style={styles.empty}>No conversations yet. Start the bot and send a WhatsApp message!</p>
        ) : (
          <div style={styles.chatList}>
            {recentChats.map((c) => {
              const last = c.lastMessage;
              const lead = c.lead;
              const phone = formatPhone(c.phone);
              return (
                <div key={c._id} style={styles.chatRow}>
                  <div style={styles.avatar}>{lead?.name?.[0]?.toUpperCase() || '?'}</div>
                  <div style={styles.chatInfo}>
                    <span style={styles.chatName}>{lead?.name || phone}</span>
                    <span style={styles.chatLast}>
                      {last ? (last.role === 'assistant' ? '🤖 ' : '👤 ') + last.content.slice(0, 60) + '...' : 'No messages'}
                    </span>
                  </div>
                  <div style={styles.chatMeta}>
                    <span style={{ ...styles.badge, background: statusColor(lead?.status) }}>
                      {lead?.status || 'new'}
                    </span>
                    <span style={styles.chatTime}>{formatTime(c.lastMessageAt)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function statusColor(s) {
  return { interested: '#f97316', 'follow-up': '#facc15', enrolled: '#22c55e', 'not-interested': '#475569' }[s] || '#6366f1';
}
function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const styles = {
  page: { padding: '28px 32px', flex: 1, overflowY: 'auto' },
  title: { fontSize: 22, fontWeight: 700, marginBottom: 0, color: '#f1f5f9' },
  qrContainer: { background: '#1e293b', border: '1px solid #3b82f6', borderRadius: 8, padding: 24, marginBottom: 24, textAlign: 'center', color: '#f1f5f9' },
  qrCodeWrapper: { display: 'inline-block', padding: 16, background: '#fff', borderRadius: 8, marginTop: 16 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 16, marginBottom: 32 },
  statCard: { background: '#1e293b', borderRadius: 10, padding: '20px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 },
  statIcon: { fontSize: 24 },
  statValue: { fontSize: 28, fontWeight: 700, color: '#f1f5f9' },
  statLabel: { fontSize: 12, color: '#64748b', textAlign: 'center' },
  section: { background: '#1e293b', borderRadius: 10, padding: 20, border: '1px solid #334155' },
  sectionTitle: { fontSize: 15, fontWeight: 600, marginBottom: 16, color: '#f1f5f9' },
  empty: { color: '#64748b', fontSize: 14 },
  chatList: { display: 'flex', flexDirection: 'column', gap: 2 },
  chatRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 8px', borderRadius: 8, cursor: 'pointer', borderBottom: '1px solid #0f172a' },
  avatar: { width: 38, height: 38, borderRadius: '50%', background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, flexShrink: 0 },
  chatInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' },
  chatName: { fontWeight: 600, fontSize: 14, color: '#f1f5f9' },
  chatLast: { fontSize: 12, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  chatMeta: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 },
  badge: { fontSize: 10, padding: '2px 7px', borderRadius: 10, color: '#fff', fontWeight: 600 },
  chatTime: { fontSize: 11, color: '#475569' },
};
